import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Bell, Home, Search, Star, ArrowLeft, SlidersHorizontal, LogOut, Settings } from 'lucide-react';
import api from '../../lib/api';
import { auditLogout } from '../../lib/auth';
import { useAdminNav, flattenNav, matchTool } from '../../design-system/adminNavCatalog';
import { DnaMobileFilterSheet } from '../dna-mobile/DnaResponsive';
import { AdminMobileDrawer } from './AdminMobileDrawer';
import { useAdminNavPrefs, clearAdminNavRecents, adminShellPresence } from './useAdminNavPrefs';
import './adminMobile.css';

const readUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };

function ContextSheet({ open, onClose, selectors }) {
  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (open) setDraft({ u: selectors.unidadeSelecionada?.id || '', p: selectors.periodoSelecionado?.id || '', s: selectors.segmentoSelecionado?.id || '' });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const field = (k, label, items, all) => (
    <label className="adm-m-field">
      <span>{label}</span>
      <select value={draft[k] || ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} data-testid={`adm-m-ctx-${k}`}>
        <option value="">{all}</option>
        {items.map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
      </select>
    </label>
  );
  const apply = () => {
    selectors.onUnidadeChange(selectors.unidades.find((x) => x.id === draft.u) || null);
    selectors.onPeriodoChange(selectors.periodos.find((x) => x.id === draft.p) || null);
    selectors.onSegmentoChange(selectors.segmentos.find((x) => x.id === draft.s) || null);
    onClose();
  };
  return (
    <DnaMobileFilterSheet open={open} onClose={onClose} title="Contexto de trabalho" testId="adm-m-ctx-sheet"
      footer={<div className="adm-m-sheet-actions">
        <button type="button" className="adm-m-btn ghost" onClick={() => setDraft({})} data-testid="adm-m-ctx-clear">Limpar</button>
        <button type="button" className="adm-m-btn" onClick={apply} data-testid="adm-m-ctx-apply">Aplicar</button>
      </div>}>
      {field('p', 'Período', selectors.periodos, 'Todos os períodos')}
      {field('u', 'Unidade', selectors.unidades, 'Todas as unidades')}
      {field('s', 'Segmento', selectors.segmentos, 'Todos os segmentos')}
    </DnaMobileFilterSheet>
  );
}

const Tab = ({ id, label, Ico, onClick, badge, active }) => (
  <button type="button" className={`adm-m-tab ${active === id ? 'on' : ''}`} onClick={onClick} aria-current={active === id ? 'page' : undefined} data-testid={`adm-m-tab-${id}`}>
    <span className="adm-m-tab-ico"><Ico className="w-5 h-5" aria-hidden />{badge > 0 && <span className="adm-m-badge">{badge > 99 ? '99+' : badge}</span>}</span>
    <span>{label}</span>
  </button>
);

function BottomBar({ active, onHome, onSearch, onFavs, onAlerts, onMenu, alerts }) {
  return (
    <nav className="adm-m-bottombar" aria-label="Navegação principal" data-testid="adm-m-bottombar">
      <Tab id="home" label="Início" Ico={Home} onClick={onHome} active={active} />
      <Tab id="search" label="Buscar" Ico={Search} onClick={onSearch} active={active} />
      <Tab id="favs" label="Favoritos" Ico={Star} onClick={onFavs} active={active} />
      <Tab id="alerts" label="Alertas" Ico={Bell} onClick={onAlerts} badge={alerts} active={active} />
      <Tab id="menu" label="Menu" Ico={Menu} onClick={onMenu} active={active} />
    </nav>
  );
}

export default function AdminMobileShell({ selectors, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const sections = useAdminNav();
  const flat = useMemo(() => flattenNav(sections), [sections]);
  const tool = matchTool(flat, location.pathname);
  const prefs = useAdminNavPrefs();
  const { pushRecent } = prefs;
  const [drawer, setDrawer] = useState({ open: false, mode: 'menu' });
  const [ctxOpen, setCtxOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [alerts, setAlerts] = useState(0);
  const triggerRef = useRef(null);
  const user = readUser();

  useEffect(() => {
    adminShellPresence.enter();
    document.body.classList.add('adm-mobile-web');
    return () => { adminShellPresence.leave(); document.body.classList.remove('adm-mobile-web'); document.documentElement.classList.remove('adm-m-lock'); };
  }, []);

  useEffect(() => { if (tool) pushRecent(tool.path); }, [tool?.path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDrawer((d) => (d.open ? { ...d, open: false } : d)); setAcctOpen(false); }, [location.pathname]);
  useEffect(() => { document.documentElement.classList.toggle('adm-m-lock', drawer.open); }, [drawer.open]);

  useEffect(() => {
    const on = () => { triggerRef.current = document.activeElement; setDrawer({ open: true, mode: 'menu' }); };
    window.addEventListener('dna:adm-open-menu', on);
    return () => window.removeEventListener('dna:adm-open-menu', on);
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = () => api.get('/dnacore/v3/alerts').then((r) => alive && setAlerts(r.data.total || 0)).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const openDrawer = (mode) => (e) => { triggerRef.current = e?.currentTarget || document.activeElement; setDrawer({ open: true, mode }); };
  const closeDrawer = useCallback((next) => {
    if (next === 'menu') { setDrawer({ open: true, mode: 'menu' }); return; }
    setDrawer((d) => ({ ...d, open: false }));
    setTimeout(() => triggerRef.current?.focus?.({ preventScroll: true }), 0);
  }, []);

  const logout = () => {
    auditLogout();
    clearAdminNavRecents();
    try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch { /* noop */ }
    navigate('/login');
  };

  const isDetail = tool && location.pathname !== tool.path;
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(tool.path));
  const ctxLabel = selectors && [selectors.periodoSelecionado?.nome || 'Todos os períodos', selectors.unidadeSelecionada?.nome || 'Todas as unidades', selectors.segmentoSelecionado?.nome].filter(Boolean).join(' · ');
  const activeTab = drawer.open ? (drawer.mode === 'search' ? 'search' : drawer.mode === 'favs' ? 'favs' : 'menu') : (location.pathname === '/v2/dashboard' ? 'home' : null);

  return (
    <div className="adm-m" data-testid="adm-m-shell">
      <a href="#adm-m-main" className="adm-m-skip">Pular para o conteúdo</a>
      <header className="adm-m-header" data-testid="adm-m-header">
        <button type="button" className="adm-m-iconbtn" onClick={openDrawer('menu')} aria-label="Abrir menu" aria-expanded={drawer.open} aria-controls="adm-m-drawer" data-testid="adm-m-menu-btn">
          <Menu className="w-5 h-5" aria-hidden />
        </button>
        {isDetail ? (
          <button type="button" className="adm-m-iconbtn" onClick={goBack} aria-label={`Voltar para ${tool.label}`} data-testid="adm-m-back-btn"><ArrowLeft className="w-5 h-5" aria-hidden /></button>
        ) : (
          <img src="/novo-dna-logo.png" alt="NOVO DNA" className="adm-m-logo sm" />
        )}
        <div className="adm-m-title">
          <strong data-testid="adm-m-title">{tool?.label || 'DNACore'}</strong>
          {selectors && <button type="button" className="adm-m-ctx" onClick={() => setCtxOpen(true)} data-testid="adm-m-ctx-btn" aria-label={`Contexto: ${ctxLabel}. Alterar`}>
            <SlidersHorizontal className="w-3 h-3" aria-hidden /> <span>{ctxLabel}</span>
          </button>}
        </div>
        <div className="adm-m-acct">
          <button type="button" className="adm-m-avatar btn" onClick={() => setAcctOpen((v) => !v)} aria-expanded={acctOpen} aria-label="Conta" data-testid="adm-m-account-btn">
            {(user.name || user.email || '?').charAt(0).toUpperCase()}
          </button>
          {acctOpen && (
            <div className="adm-m-pop" role="menu" data-testid="adm-m-account-menu">
              <div className="adm-m-pop-head"><strong>{user.name || 'Usuário'}</strong><span>{user.email}</span>{user.role && <em>{user.role}</em>}</div>
              <button type="button" role="menuitem" onClick={() => navigate('/admin')} data-testid="adm-m-account-settings"><Settings className="w-4 h-4" aria-hidden /> Configurações</button>
              <button type="button" role="menuitem" className="danger" onClick={logout} data-testid="adm-m-account-logout"><LogOut className="w-4 h-4" aria-hidden /> Sair</button>
            </div>
          )}
        </div>
      </header>

      <div id="adm-m-main" tabIndex={-1} className="adm-m-content">{children}</div>

      <BottomBar active={activeTab} alerts={alerts}
        onHome={() => { setDrawer((d) => ({ ...d, open: false })); navigate('/v2/dashboard'); }}
        onSearch={openDrawer('search')} onFavs={openDrawer('favs')} onMenu={openDrawer('menu')}
        onAlerts={() => window.dispatchEvent(new CustomEvent('dna:open-alerts'))} />

      <AdminMobileDrawer open={drawer.open} mode={drawer.mode} onClose={closeDrawer} sections={sections} prefs={prefs} user={user} onLogout={logout} />
      {selectors && <ContextSheet open={ctxOpen} onClose={() => setCtxOpen(false)} selectors={selectors} />}
    </div>
  );
}
